/**
 * Timeline Component
 *
 * Generic component to display a chronological list of events/activities.
 */

import type { ReactNode, ElementType } from 'react';
import { Clock, User } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface TimelineItem {
  id: string;
  /** Main content/action description */
  content: string | ReactNode;
  /** User who performed the action (optional) */
  user?: string;
  /** Timestamp/date string */
  date: string;
  /** Custom icon for this item */
  icon?: ElementType;
  /** Color for the timeline dot */
  dotColor?: string;
}

export interface TimelineProps {
  /** List of timeline items */
  items: TimelineItem[];
  /** Empty state message */
  emptyMessage?: string;
  /** Additional class names */
  className?: string;
  /** Show user info (default: true) */
  showUser?: boolean;
  /** Custom dot color (default: primary-500) */
  dotColor?: string;
}

export function Timeline({
  items,
  emptyMessage = 'Aucune activite enregistree',
  className,
  showUser = true,
  dotColor = 'bg-primary-500',
}: TimelineProps) {
  if (items.length === 0) {
    return (
      <p className="text-sm text-neutral-500 text-center py-8">
        {emptyMessage}
      </p>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {items.map((item, index) => (
        <div
          key={item.id}
          className="flex gap-4 pb-4 border-b border-neutral-100 last:border-0 last:pb-0"
        >
          {/* Timeline dot and line */}
          <div className="flex flex-col items-center">
            <div
              className={cn(
                'w-2 h-2 rounded-full mt-2',
                item.dotColor || dotColor
              )}
            />
            {index < items.length - 1 && (
              <div className="w-px flex-1 bg-neutral-200 mt-2" />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="text-sm text-dark-500 mb-1">
              {item.content}
            </div>
            <div className="flex items-center gap-3 text-xs text-neutral-500">
              {showUser && item.user && (
                <>
                  <span className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {item.user}
                  </span>
                  <span>|</span>
                </>
              )}
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {item.date}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
